import React, { useState, useEffect } from 'react';
import axios from 'axios';

const STATUS_PRIORITY = {
  open: 0,
  in_progress: 1,
  resolved: 2
};

const sortFeedbackByStatus = (feedback) => {
  return [...feedback].sort((a, b) => {
    const statusDiff = (STATUS_PRIORITY[a.status] ?? Number.MAX_SAFE_INTEGER) - (STATUS_PRIORITY[b.status] ?? Number.MAX_SAFE_INTEGER);
    if (statusDiff !== 0) {
      return statusDiff;
    }

    return new Date(b.createdAt) - new Date(a.createdAt);
  });
};

const typeBadge = (type) => {
  const styles = {
    bug: 'bg-red-100 text-red-800',
    feature: 'bg-yellow-100 text-yellow-800',
    general: 'bg-blue-100 text-blue-800'
  };
  const labels = { bug: '🐛 Bug', feature: '💡 Feature', general: '💬 General' };
  return (
    <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${styles[type] || ''}`}>
      {labels[type] || type}
    </span>
  );
};

const statusBadge = (status) => {
  const styles = {
    open: 'bg-green-100 text-green-800',
    in_progress: 'bg-yellow-100 text-yellow-800',
    resolved: 'bg-gray-100 text-gray-800'
  };
  const label = status.replace('_', ' ').toUpperCase();
  return (
    <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${styles[status] || ''}`}>
      {label}
    </span>
  );
};

const FeedbackManagement = () => {
  const [feedbackList, setFeedbackList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Detail modal
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Admin note modal
  const [noteTarget, setNoteTarget] = useState(null);
  const [adminNote, setAdminNote] = useState('');
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [savingNote, setSavingNote] = useState(false);

  // Screenshot modal
  const [screenshotUrl, setScreenshotUrl] = useState(null);

  useEffect(() => {
    fetchFeedback();
  }, []);

  const fetchFeedback = async () => {
    try {
      const res = await axios.get('/api/feedback');
      setFeedbackList(sortFeedbackByStatus(res.data.feedback || []));
    } catch (err) {
      setError('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const res = await axios.put(`/api/feedback/${id}/status`, { status: newStatus });
      if (res.data.success) {
        setFeedbackList(prev =>
          sortFeedbackByStatus(
            prev.map(f => f.id === id ? { ...f, status: newStatus, resolvedAt: res.data.resolvedAt } : f)
          )
        );
      }
    } catch (err) {
      alert('Failed to update status');
    }
  };

  const openDetail = async (id) => {
    try {
      const res = await axios.get(`/api/feedback/${id}`);
      setSelectedFeedback(res.data.feedback);
      setShowDetailModal(true);
    } catch (err) {
      alert('Failed to load feedback details');
    }
  };

  const openNoteModal = (feedback) => {
    setNoteTarget(feedback);
    setAdminNote(feedback.adminNotes || '');
    setShowNoteModal(true);
  };

  const submitAdminNote = async () => {
    if (!adminNote.trim()) {
      alert('Please enter an admin note');
      return;
    }

    setSavingNote(true);
    try {
      const res = await axios.put(`/api/feedback/${noteTarget.id}/admin-note`, { adminNote: adminNote.trim() });
      if (res.data.success) {
        setFeedbackList(prev =>
          prev.map(f => f.id === noteTarget.id ? { ...f, adminNotes: adminNote.trim() } : f)
        );
        setShowNoteModal(false);
      }
    } catch (err) {
      alert('Failed to save admin note');
    } finally {
      setSavingNote(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-gray-500">Loading feedback...</div>;
  }

  return (
    <div className="px-4 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Feedback Management</h1>
        <p className="text-sm text-gray-500 mt-1">Review and manage user feedback, bug reports, and feature requests</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">{error}</div>
      )}

      {feedbackList.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center text-gray-500">
          No feedback submitted yet.
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {feedbackList.map((fb) => (
                  <tr key={fb.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">{fb.username}</div>
                      {fb.user && <div className="text-xs text-gray-500">{fb.user.email}</div>}
                    </td>
                    <td className="px-4 py-3">{typeBadge(fb.type)}</td>
                    <td className="px-4 py-3">
                      <div
                        className="text-sm text-gray-900 max-w-xs truncate cursor-pointer hover:text-blue-600"
                        title="Click to view full feedback"
                        onClick={() => openDetail(fb.id)}
                      >
                        {fb.description}
                      </div>
                      {fb.screenshotPath && (
                        <span
                          className="text-xs text-purple-600 cursor-pointer hover:text-purple-800 hover:underline"
                          onClick={() => setScreenshotUrl(`/uploads/${fb.screenshotPath}`)}
                        >
                          📎 Screenshot
                        </span>
                      )}
                      {fb.adminNotes && <span className="ml-1 text-xs text-blue-600">📝 Note</span>}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={fb.status}
                        onChange={(e) => handleStatusChange(fb.id, e.target.value)}
                        className={`text-xs px-2 py-1 rounded-full border-0 focus:ring-2 focus:ring-blue-500 ${
                          fb.status === 'open' ? 'bg-green-100 text-green-800' :
                          fb.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}
                      >
                        <option value="open">OPEN</option>
                        <option value="in_progress">IN PROGRESS</option>
                        <option value="resolved">RESOLVED</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs text-gray-500">{new Date(fb.createdAt).toLocaleDateString()}</div>
                      {fb.resolvedAt && (
                        <div className="text-xs text-green-600">Resolved: {new Date(fb.resolvedAt).toLocaleDateString()}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openDetail(fb.id)}
                          className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          View
                        </button>
                        <button
                          onClick={() => openNoteModal(fb)}
                          className="text-xs px-2 py-1 bg-gray-600 text-white rounded hover:bg-gray-700"
                        >
                          Note
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedFeedback && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={() => setShowDetailModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-lg font-bold text-gray-900">Feedback Details</h2>
                <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500">User</label>
                    <p className="text-sm text-gray-900">{selectedFeedback.username}</p>
                    {selectedFeedback.user && <p className="text-xs text-gray-500">{selectedFeedback.user.email}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500">Type</label>
                    {typeBadge(selectedFeedback.type)}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500">Status</label>
                    {statusBadge(selectedFeedback.status)}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500">Submitted</label>
                    <p className="text-sm text-gray-900">{new Date(selectedFeedback.createdAt).toLocaleString()}</p>
                    {selectedFeedback.resolvedAt && (
                      <p className="text-xs text-green-600">Resolved: {new Date(selectedFeedback.resolvedAt).toLocaleString()}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedFeedback.description}</p>
                  </div>
                </div>

                {selectedFeedback.screenshotPath && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Screenshot</label>
                    <div className="flex items-center space-x-3">
                      <img
                        src={`/uploads/${selectedFeedback.screenshotPath}`}
                        alt="Feedback screenshot"
                        className="max-w-xs max-h-48 rounded-lg border border-gray-300 cursor-pointer"
                        onClick={() => setScreenshotUrl(`/uploads/${selectedFeedback.screenshotPath}`)}
                      />
                      <button
                        onClick={() => setScreenshotUrl(`/uploads/${selectedFeedback.screenshotPath}`)}
                        className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700"
                      >
                        View Full Size
                      </button>
                    </div>
                  </div>
                )}

                {selectedFeedback.adminNotes && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Admin Notes</label>
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedFeedback.adminNotes}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Note Modal */}
      {showNoteModal && noteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={() => setShowNoteModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-lg font-bold text-gray-900">Admin Note</h2>
                <button onClick={() => setShowNoteModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
              </div>
              <p className="text-xs text-gray-500 mb-3">Feedback #{noteTarget.id} by {noteTarget.username}</p>
              <textarea
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                placeholder="Add admin notes about this feedback..."
              />
              <div className="flex justify-end space-x-2 mt-4">
                <button
                  onClick={() => setShowNoteModal(false)}
                  className="px-3 py-1.5 text-sm text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={submitAdminNote}
                  disabled={savingNote}
                  className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {savingNote ? 'Saving...' : 'Save Note'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Full-size Screenshot Modal */}
      {screenshotUrl && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-75" onClick={() => setScreenshotUrl(null)}>
          <div className="relative max-w-[90vw] max-h-[90vh]">
            <button
              onClick={() => setScreenshotUrl(null)}
              className="absolute -top-3 -right-3 bg-white text-gray-800 rounded-full w-8 h-8 flex items-center justify-center hover:bg-gray-100 shadow text-lg"
            >
              &times;
            </button>
            <img src={screenshotUrl} alt="Full size screenshot" className="max-w-full max-h-[85vh] rounded-lg" />
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackManagement;
